/**
 * Vision — React wrapper for the imperative `lib/pilot/vision.js`
 * sticky-canvas scroll-driven graph.
 *
 * Phase 7 of the React-engine port (`docs/eng/pilot-react-engine.md`).
 *
 * Like Demo, the canvas DRAWING (sprites, batched edge strokes, the
 * rAF loop, pointer-drag handlers) stays imperative — canvas pixels
 * aren't React's domain, per the architectural-invariant carve-out.
 * This component owns the *lifecycle* and the *DOM hand-off*:
 *
 *   - Mount the `<section id="vision">` plus all of its chrome
 *     (vis-vignette, vis-overlay → vis-card with title/body/final,
 *     vis-legend, vis-progress, vis-hint, vis-drag-hint) as a single
 *     React subtree.
 *   - Once mounted, hand the refs (canvas + section + the engine's
 *     six cached text/progress nodes) to `initVision({...})`. The
 *     engine builds its graph, sprites, edge buckets, attaches scroll/
 *     resize/pointer listeners, and starts the rAF loop.
 *   - On unmount, `teardownVision()` cancels the rAF, removes every
 *     listener, clears the cached refs, and resets the module-level
 *     `initialized` flag so the next mount rebuilds from scratch.
 *
 * The Pilot context's `viewMode === 'vision'` predicate guards this
 * component's mount upstream (in `VisionSection.tsx`). PilotProvider
 * owns the `vis-mode` body class; this component does not touch it.
 */

import React, {useEffect, useRef} from 'react';
import {initVision, teardownVision} from '../../lib/pilot/vision.js';

export default function Vision() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const finalRef = useRef<HTMLDivElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      !sectionRef.current ||
      !canvasRef.current ||
      !titleRef.current ||
      !bodyRef.current ||
      !cardRef.current ||
      !finalRef.current ||
      !progressFillRef.current
    ) {
      return;
    }

    initVision({
      canvas: canvasRef.current,
      section: sectionRef.current,
      titleEl: titleRef.current,
      bodyEl: bodyRef.current,
      cardEl: cardRef.current,
      finalEl: finalRef.current,
      progressFillEl: progressFillRef.current,
    });

    return () => {
      teardownVision();
    };
  }, []);

  return (
    // F24 (Phase K): The Vision narrative is a *scroll-driven* canvas
    // sequence — vision.js's draw loop reads `visSection.getBoundingClientRect()`
    // and divides scroll progress across SCENES (currently 8 entries).
    // For that math to fire at all, the section itself needs to be tall
    // enough to scroll past. The sticky inner container holds the
    // canvas pinned to the viewport while the visitor scrolls through
    // ~8 viewports of section height — one per scene boundary.
    <section
      ref={sectionRef}
      // `isolate` (CSS `isolation: isolate`) contains the vis-card
      // `z-[2]` and other z-indexed children inside the section's own
      // stacking context. Matches the same hardening applied to
      // <Demo> so navbar z-indexing isn't competed with by takeover
      // descendants in the root stacking context.
      className="vision isolate min-h-[800vh]"
      id="vision"
    >
      <div className="vis-sticky sticky top-0 h-screen w-full overflow-hidden bg-taupe-950">
        <canvas
          ref={canvasRef}
          id="vis-canvas"
          className="absolute inset-0 h-full w-full"
        />
        <div className="vis-vignette pointer-events-none absolute inset-0 bg-[radial-gradient(closest-side,transparent,rgba(0,0,0,0.6))]" />
        <div
          // Card pins to the bottom-center of the sticky canvas so
          // the dots-tree above reads as the primary visual and the
          // text/legend sit as a stable "ground floor" the visitor
          // can read without the card obscuring the center of the
          // network. `pb-24` leaves clearance for the
          // `vis-progress` bar + the `vis-hint` / `vis-drag-hint`
          // copy at the absolute bottom.
          className="vis-overlay absolute inset-0 flex flex-col items-center justify-end gap-6 p-12 pb-24 text-center"
        >
          <div
            ref={cardRef}
            // F17 Slice C: the prior `.vis-card` CSS rule
            // (position/z-index/bg/backdrop-filter/shadow) is inlined
            // here so pilot-engine.css can shed it. The card must sit
            // ABOVE the scrolly canvas (z-2) with a translucent dark
            // ground + blur so the dots-tree behind doesn't read
            // through into the text.
            className="vis-card relative z-[2] max-w-2xl rounded-2xl [background:oklch(14.7%_0.004_49.3/0.78)] p-8 text-taupe-50 [backdrop-filter:blur(8px)] [-webkit-backdrop-filter:blur(8px)] shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
            id="vis-card"
          >
            <h2
              ref={titleRef}
              id="vis-title"
              className="mb-3 font-display text-3xl/10 text-taupe-50"
            >
              A single internal process
            </h2>
            <p
              ref={bodyRef}
              id="vis-body"
              className="text-base/7 text-taupe-300"
            >
              Request submitted, reviewed, approved, fulfilled. A handful of
              roles, mostly human handoffs.
            </p>
          </div>
          <div
            ref={finalRef}
            className="vis-final hidden font-display text-3xl/10 text-taupe-50"
            id="vis-final"
          >
            From a process, to institution interconnectivity, to the Planapus
            Network.
          </div>
        </div>
        <div className="vis-legend absolute bottom-6 left-6 flex flex-col gap-1 font-mono text-xs/5 text-taupe-300">
          {/* Legend swatches match the canvas node palette in vision.js:
              human → amber (245,160,86), automated → teal (109,214,255),
              trigger → magenta (255,93,143). Keep these in sync if the
              halo sprite colors in vision.js change. */}
          <span className="inline-flex items-center gap-2">
            <i className="dot human inline-block size-2 rounded-full bg-[rgb(245,160,86)]" />{' '}
            Human routing
          </span>
          <span className="inline-flex items-center gap-2">
            <i className="dot auto inline-block size-2 rounded-full bg-[rgb(109,214,255)]" />{' '}
            Planapus-automated
          </span>
          <span className="inline-flex items-center gap-2">
            <i className="dot trigger inline-block size-2 rounded-full bg-[rgb(255,93,143)]" />{' '}
            External trigger
          </span>
        </div>
        <div className="vis-progress absolute bottom-0 left-0 right-0 h-1 bg-taupe-50/10">
          <div
            ref={progressFillRef}
            className="vis-progress-fill h-full bg-taupe-50"
            id="vis-progress-fill"
            style={{width: '0%'}}
          />
        </div>
        <div className="vis-hint absolute bottom-6 right-6 font-mono text-xs/5 text-taupe-300">
          Scroll ↓
        </div>
        <div
          className="vis-drag-hint absolute bottom-16 right-6 hidden font-mono text-xs/5 text-taupe-300"
          id="vis-drag-hint"
        >
          ✋ Drag to look around
        </div>
      </div>
    </section>
  );
}
